// src/services/api/adminAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Admin API service for managing access requests and tenant creation
 */
export const adminAPI = {
  /**
   * Get all access requests (admin only)
   * @returns {Promise<Object>} API response with requests array
   */
  async getAccessRequests() {
    try {
      console.log('API: Getting access requests');
      
      const headers = await createAuthHeaders();
      
      const response = await fetch(`${FUNCTIONS_BASE_URL}/getAccessRequests`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('API: Successfully retrieved access requests', {
        count: result.requests?.length || 0
      });
      
      return result;
      
    } catch (error) {
      console.error('API Error in getAccessRequests:', error);
      throw new Error(`Failed to get access requests: ${error.message}`);
    }
  },

  /**
   * Approve an access request and create tenant
   * @param {string} requestId - Access request ID
   * @returns {Promise<Object>} API response
   */
  async approveAccessRequest(requestId) {
    try {
      console.log('API: Approving access request', { requestId });
      
      const headers = await createAuthHeaders();
      
      const response = await fetch(`${FUNCTIONS_BASE_URL}/approveAccessRequest`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ requestId })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('API: Successfully approved access request', {
        requestId,
        newUserId: result.userId
      });
      
      return result;
      
    } catch (error) {
      console.error('API Error in approveAccessRequest:', error);
      throw new Error(`Failed to approve access request: ${error.message}`);
    }
  },

  /**
   * Reject an access request
   * @param {string} requestId - Access request ID
   * @param {string} reason - Rejection reason (optional)
   * @returns {Promise<Object>} API response
   */
  async rejectAccessRequest(requestId, reason = '') {
    try {
      console.log('API: Rejecting access request', { requestId, reason });
      
      const headers = await createAuthHeaders();
      
      const response = await fetch(`${FUNCTIONS_BASE_URL}/rejectAccessRequest`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ requestId, reason })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('API: Successfully rejected access request', {
        requestId
      });
      
      return result;
      
    } catch (error) {
      console.error('API Error in rejectAccessRequest:', error);
      throw new Error(`Failed to reject access request: ${error.message}`);
    }
  },

  async growRegistration(data) {
    const headers = await createAuthHeaders();

    const response = await fetch(`${FUNCTIONS_BASE_URL}/growRegistration`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    return response.json();
  },

  /**
   * Check if current user has admin privileges
   * @returns {Promise<Object>} API response with admin status
   */
  async checkAdminStatus() {
    try {
      console.log('API: Checking admin status');

      const headers = await createAuthHeaders();

      const response = await fetch(`${FUNCTIONS_BASE_URL}/checkAdminStatus`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('API: Successfully checked admin status', {
        isAdmin: result.isAdmin
      });

      return result;

    } catch (error) {
      console.error('API Error in checkAdminStatus:', error);
      throw new Error(`Failed to check admin status: ${error.message}`);
    }
  },

  /**
   * Get monthly message usage for a specific user (admin only)
   * @param {string} targetUserId - User ID to fetch usage for
   * @param {string} startMonth - Start month in YYYY-MM format
   * @param {string} endMonth - End month in YYYY-MM format
   * @returns {Promise<Object>} API response with usage data
   */
  async getUserMessageUsage(targetUserId, startMonth, endMonth) {
    try {
      console.log('API: Getting user message usage', { targetUserId, startMonth, endMonth });

      const headers = await createAuthHeaders();

      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/getUserMessageUsage?targetUserId=${encodeURIComponent(targetUserId)}&startMonth=${encodeURIComponent(startMonth)}&endMonth=${encodeURIComponent(endMonth)}`,
        {
          method: 'GET',
          headers
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('API: Successfully retrieved user message usage', {
        targetUserId,
        automationsCount: result.data?.automations?.length || 0,
        campaignsCount: result.data?.campaigns?.length || 0
      });

      return result;

    } catch (error) {
      console.error('API Error in getUserMessageUsage:', error);
      throw new Error(`Failed to get user message usage: ${error.message}`);
    }
  },

  /**
   * Get incoming WhatsApp message conversations (admin only)
   * Returns list of conversations with only the most recent message per sender
   * @returns {Promise<Object>} API response with conversations array
   */
  async getIncomingMessageConversations() {
    try {
      console.log('API: Getting incoming message conversations');

      const headers = await createAuthHeaders();

      const response = await fetch(`${FUNCTIONS_BASE_URL}/getIncomingMessageConversations`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('API: Successfully retrieved incoming message conversations', {
        count: result.conversations?.length || 0
      });

      return result;

    } catch (error) {
      console.error('API Error in getIncomingMessageConversations:', error);
      throw new Error(`Failed to get incoming message conversations: ${error.message}`);
    }
  },

  /**
   * Get all messages for a specific sender (admin only)
   * @param {string} senderNumber - Sender WhatsApp number (e.g., "whatsapp:+1234567890")
   * @returns {Promise<Object>} API response with messages array
   */
  async getConversationMessages(senderNumber) {
    try {
      console.log('API: Getting conversation messages', { senderNumber });

      const headers = await createAuthHeaders();

      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/getConversationMessages?senderNumber=${encodeURIComponent(senderNumber)}`,
        {
          method: 'GET',
          headers
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('API: Successfully retrieved conversation messages', {
        senderNumber,
        count: result.messages?.length || 0
      });

      return result;

    } catch (error) {
      console.error('API Error in getConversationMessages:', error);
      throw new Error(`Failed to get conversation messages: ${error.message}`);
    }
  },

  /**
   * Send WhatsApp template message
   * @param {Object} params - Template send parameters
   * @param {string} params.phoneNumber - Recipient phone number
   * @param {string} params.contentSid - Twilio content SID
   * @param {Object} params.variables - Variables object with positions as keys (e.g., {"1": "John", "2": "3pm"})
   * @returns {Promise<Object>} API response
   */
  async sendWhatsAppTemplate({ phoneNumber, contentSid, variables }) {
    try {
      console.log('API: Sending WhatsApp template', { phoneNumber, contentSid, variables });

      const headers = await createAuthHeaders();

      const response = await fetch(`${FUNCTIONS_BASE_URL}/sendWhatsAppTemplate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          phoneNumber,
          contentSid,
          variables
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('API: Successfully sent WhatsApp template', {
        messageSid: result.messageSid
      });

      return result;

    } catch (error) {
      console.error('API Error in sendWhatsAppTemplate:', error);
      throw new Error(`Failed to send WhatsApp template: ${error.message}`);
    }
  },

  /**
   * Send free-text WhatsApp message within 24-hour window
   * @param {Object} params - Message parameters
   * @param {string} params.phoneNumber - Recipient phone number (format: whatsapp:+1234567890)
   * @param {string} params.message - Message text content
   * @returns {Promise<Object>} API response with message details
   */
  async sendWhatsAppFreeText({ phoneNumber, message }) {
    try {
      console.log('API: Sending WhatsApp free-text message', { phoneNumber, messageLength: message?.length });

      const headers = await createAuthHeaders();

      const response = await fetch(`${FUNCTIONS_BASE_URL}/sendWhatsAppFreeText`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          phoneNumber,
          message
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('API: Successfully sent WhatsApp free-text message', {
        messageSid: result.messageSid
      });

      return result;

    } catch (error) {
      console.error('API Error in sendWhatsAppFreeText:', error);
      throw new Error(`Failed to send WhatsApp message: ${error.message}`);
    }
  }
};